import { useQueryClient } from '@tanstack/react-query';
import { appRoutes, jobIdRoute, queryKeys } from '@utils/consts';
import { useLocation, useParams } from 'react-router';
import { JobRefetchOptions, UpdateJobProps, UpdateStagesProps } from './types';
import { useQueryParams } from '@hooks/queryParams';
import { JobStagesRespSuccess } from '@services/jobs/stages/types';
import { Job, JobStage, JobSubtask, JobTask } from '@root/globalTypes';
import { JobRespSuccess } from '@services/jobs/fetchJob';
import { useAccess } from '@hooks/access';

export const useJobRefetch = (options: JobRefetchOptions = {}) => {
  const access = useAccess();

  const { pathname } = useLocation();
  const { projectId, jobId: id } = useParams();
  const queryClient = useQueryClient();

  const {
    params: { type },
  } = useQueryParams();

  const hotSeat = pathname.includes(appRoutes.hotSeat);
  const workload = pathname.includes(appRoutes.workload);
  const jobs = pathname.includes(appRoutes.jobs);
  const job = pathname.includes('/jobs/');
  const materials = pathname.includes(jobIdRoute.materials);
  const documents = pathname.includes(jobIdRoute.documents);
  const questionnaires = pathname.includes(jobIdRoute.questionnaires);
  const workProgress = pathname.includes(jobIdRoute.workProgress);
  const { schedule } = options;

  const refetchActionQueries = async () => {
    if (access.fieldUser) await queryClient.invalidateQueries({ queryKey: queryKeys.foremanTask });
    if (hotSeat) await queryClient.invalidateQueries({ queryKey: queryKeys.hotSeat });
    if (jobs) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dropdowns.stages });
    }
    if (job || documents || materials || workProgress || questionnaires) {
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.jobProject, projectId] });
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.job, projectId, id] });
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.jobStages, projectId, id] });
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.questionnaires.index, projectId, id],
      });
    }
    if (materials) await queryClient.invalidateQueries({ queryKey: queryKeys.materials });
    if (documents) await queryClient.invalidateQueries({ queryKey: queryKeys.documents });
    if (workload && !schedule) {
      if (type === 'day')
        await queryClient.invalidateQueries({ queryKey: queryKeys.schedules.internal.index });
      await queryClient.invalidateQueries({ queryKey: queryKeys.workloadCalendarSidebar });
    }
    if (questionnaires) {
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.questionnaire, projectId, id, options.taskId],
      });
    }
  };

  const refetchScheduleQueries = async () => {
    const { schedule } = options;
    if (!schedule) return;

    if (schedule.type === 'external') {
      await queryClient.invalidateQueries({ queryKey: queryKeys.dropdowns.companies });
      if (workload && ['create', 'remove'].includes(schedule?.action || ''))
        await queryClient.invalidateQueries({
          queryKey: [...queryKeys.workloadCalendarSidebar, 'jobs'],
        });
    }

    if (schedule.type === 'internal') {
      queryClient.removeQueries({ queryKey: queryKeys.schedules.internal.item });
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.internal.index });
      if (workload) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workload });
        if (type === 'two-week')
          queryClient.invalidateQueries({ queryKey: queryKeys.workloadTwoWeeklyCalendar });
        if (type === 'month')
          queryClient.invalidateQueries({
            queryKey: queryKeys.workloadMonthlyCalendar,
          });
        if (!type || type === 'week')
          queryClient.invalidateQueries({ queryKey: queryKeys.workloadWeeklyCalendar });
        if (type === 'day') queryClient.invalidateQueries({ queryKey: queryKeys.workloadDaily });
        if (['create', 'remove'].includes(schedule?.action || ''))
          queryClient.invalidateQueries({ queryKey: queryKeys.workloadCalendarSidebar });
      }
    }
  };

  const getJob = (jobId?: string) =>
    queryClient.getQueryData<JobRespSuccess>([...queryKeys.job, projectId, jobId || id])?.data;

  const restoreJob = (jobId?: string, data?: Job) => {
    queryClient.setQueryData([...queryKeys.job, projectId, jobId || id], { success: true, data });
  };

  const updateJob = async ({ jobId, data }: UpdateJobProps) => {
    await queryClient.cancelQueries({ queryKey: [...queryKeys.job, projectId, jobId || id] });
    const newJob = getJob(jobId || id);

    queryClient.setQueryData<JobRespSuccess>([...queryKeys.job, projectId, jobId || id], (prev) => {
      if (!prev) return;
      return {
        ...prev,
        data: {
          ...prev.data,
          ...data,
        },
      };
    });

    return newJob;
  };

  const getStages = (jobId?: string) =>
    queryClient.getQueryData<JobStagesRespSuccess>([...queryKeys.jobStages, projectId, jobId || id])
      ?.records || [];

  const restoreStages = (jobId?: string, records?: JobStage[]) => {
    queryClient.setQueryData([...queryKeys.jobStages, projectId, jobId || id], {
      success: true,
      records,
    });
  };

  const updateStages = async ({ jobId, stage, task, subtask }: UpdateStagesProps) => {
    await queryClient.cancelQueries({ queryKey: [...queryKeys.jobStages, projectId, jobId || id] });
    const stages = getStages(jobId || id);

    queryClient.setQueryData<JobStagesRespSuccess>(
      [...queryKeys.jobStages, projectId, jobId || id],
      (prev) => {
        if (!prev) return;
        return {
          ...prev,
          records: prev.records.map((a) => {
            const newStage = a.id === stage?.id ? { ...a, ...stage } : a;
            return {
              ...newStage,
              tasks: newStage.tasks.map((b) => ({
                ...b,
                tasks: b.tasks.map((c) => {
                  const newTask = c.id === task?.id ? { ...c, ...task } : c;
                  return {
                    ...newTask,
                    subtasks: newTask.subtasks?.map((d) =>
                      d.id === subtask?.id ? { ...d, ...subtask } : d,
                    ),
                  };
                }),
              })),
            };
          }),
        };
      },
    );

    return stages;
  };

  const getTask = (jobId?: string, taskId?: string) => {
    let task: JobTask | undefined;
    const stages = getStages(jobId || id);

    stages.forEach((a) =>
      a.tasks.forEach((b) =>
        b.tasks.forEach((c) => {
          if (c.id === taskId) task = c;
        }),
      ),
    );
    return task;
  };

  const getSubtask = (jobId?: string, subtaskId?: string) => {
    let subtask: JobSubtask | undefined;
    const stages = getStages(jobId || id);

    stages.forEach((a) =>
      a.tasks.forEach((b) =>
        b.tasks.forEach((c) => {
          c.subtasks?.forEach((d) => {
            if (d.id === subtaskId) subtask = d;
          });
        }),
      ),
    );
    return subtask;
  };

  return {
    refetch: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      refetchScheduleQueries();
      refetchActionQueries();
    },
    job: {
      get: getJob,
      update: updateJob,
      restore: restoreJob,
    },
    task: {
      get: getTask,
    },
    subtask: {
      get: getSubtask,
    },
    stages: {
      get: getStages,
      update: updateStages,
      restore: restoreStages,
    },
  };
};
